import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";
import { Box, Link, ListItem, Typography, Stack, List } from "@mui/material";

import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import { routes } from "@/global/routes";
import { createState } from "@/global/store/zustand_store";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect to your Prudential Center account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the suites you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://am.ticketmaster.com/prucenter/"
        >
          Prudential Center Account Manager
        </Link>{" "}
        within 24 hours of redemption. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Where are my tickets delivered?",
    description:
      "Event tickets are delivered directly within your Prudential Center Account Manager.",
  },
  {
    title: "What is a Single Night Suite Rental?",
    description:
      "A Single Night Suite Rental allows you to elevate your experience at a New Jersey Devils game, concert, or special event. It's ideal for a night out with friends and family, client entertainment, or employee outings.",
  },
  {
    title: "Which events are eligible for Single Night Rentals?",
    description:
      "Single Night Rentals are available for New Jersey Devils games, marquee concerts and events, family shows, and other special events. Availability varies by event.",
  },
  {
    title: "What premium spaces can be rented as a Single Night Rental?",
    description:
      "Based on availability, single night rentals may be available in Luxury Suites, Verizon Lounge Loge Boxes, The Lofts Loge Boxes, and Studio Suites.",
  },
  {
    title: "What is included in the price of a Single Night Rental?",
    description: (
      <>
        <List>
          <b>Luxury Suites include:</b>
          <ListItem>• A private suite to enjoy the event</ListItem>
          <ListItem>• 16-48 tickets, based on suite location</ListItem>
          <ListItem>• Starter food or beverage catering package</ListItem>
          <ListItem>• Two (2) parking passes</ListItem>
          <ListItem>• Access to VIP entrances</ListItem>
        </List>

        <List>
          <b>Loge Boxes and Studio Suites include:</b>
          <ListItem>• A semi-private box to enjoy the event</ListItem>
          <ListItem>• 6-12 tickets, based on box location</ListItem>
          <ListItem>• All-inclusive food and non-alcoholic beverages</ListItem>
          <ListItem>
            • Access to the premium lounge and amenities associated with the
            location
          </ListItem>
          <ListItem>• Access to VIP entrances</ListItem>
        </List>
      </>
    ),
  },
  {
    title:
      "Can I order additional food & beverage beyond the included Starter package for Suites?",
    description:
      "Yes. After purchase, a member of the Premium department will connect you with our catering team to place additional food and beverage orders.",
  },
  {
    title: "Is parking included with a Single Night Rental?",
    description:
      "Yes. Full Luxury Suites include two (2) parking passes, while Loge Boxes include one (1) parking pass.",
  },
  {
    title: "How are tickets delivered for Single Night Rentals?",
    description:
      "Tickets will be available in your Prudential Center Account Manager within 24 hours of purchase.",
  },
  {
    title: "Who should I contact with questions before or after purchase?",
    description: (
      <>
        Contact the Prudential Center & New Jersey Devils Premium Department at{" "}
        <Link
          href="mailto:premium@prucenter.com"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          premium@prucenter.com
        </Link>{" "}
        or by calling{" "}
        <Link
          href="tel:9737576464"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          973-757-6464
        </Link>
        .
      </>
    ),
  },
];

const clubSeatingImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Club%20Seating%20-%20The%20Lofts/Loft%20Club%20Seat.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Club%20Seating%20-%20The%20Lofts/Loft%20Lounge%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Club%20Seating%20-%20The%20Lofts/Loft%20Lounge%20-%202.jpg",
];

const loftTablesImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loft%20Tables%20-%20The%20Lofts/Loft%20Lounge%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loft%20Tables%20-%20The%20Lofts/Loft%20Lounge%20-%202.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loft%20Tables%20-%20The%20Lofts/Loft%20Table.jpg",
];

const logeBoxesImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loge%20Boxes%20-%20The%20Lofts/Loft%20Loge%20Box.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loge%20Boxes%20-%20The%20Lofts/Loft%20Lounge%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Loge%20Boxes%20-%20The%20Lofts/Loft%20Lounge%20-%202.jpg",
];

const luxurySuitesImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--02.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--13.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--14.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--28.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--43.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--45.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--49.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--52.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/20251029_Suite--60.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/Suites%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Luxury%20Suites/Suites%20-%202.jpg",
];

const mLoungeImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/M%20Lounge/M%20Lounge%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/M%20Lounge/M%20Lounge%20-%202.jpg",
];

const studioSuitesImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Studio%20Suites/Studio%20Suites%20-%201.jpg",
];

const verizonLoungeImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%202.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Banquette%20Seat.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Club%20Seat.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Food%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Food%202.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Food%203.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Loge%20Box%202.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20Loge%20Box.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/Prudential%20Center/Verizon%20Lounge/Verizon%20Lounge%20-%20View.jpg",
];

export const suiteImages = {
  // Verizon Lounge - Loge Boxes
  LB1: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB10: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB2: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB3: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB4: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB5: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB6: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB7: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB8: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  LB9: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],

  // Club Seating - The Lofts
  1: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  2: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  3: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  4: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  5: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  6: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  7: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],
  8: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...clubSeatingImages,
  ],

  // Loft Tables - The Lofts
  T1: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T2: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T3: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T4: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T5: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T6: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T7: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T8: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T9: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],
  T10: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...loftTablesImages,
  ],

  // Verizon Lounge - Sections
  9: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  10: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  11: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  12: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  13: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  14: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  15: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  16: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],
  17: [
    "https://3dspaces.american3dvirtualtours.com/tour/pierclub",
    ...verizonLoungeImages,
  ],

  // Studio Suites
  SS18: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS1A: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS1B: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS20: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS22: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS4A: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS4B: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS5: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS7: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],
  SS9: [
    "https://3dspaces.american3dvirtualtours.com/tour/studiosuite",
    ...studioSuitesImages,
  ],

  // Luxury Suites
  S101: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S102: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S103: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S104: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S105: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S106: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S107: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S108: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S109: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S110: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S111: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S112: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S113: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S114: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],

  S115: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S116: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S117: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S118: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S119: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S120: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S121: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S122: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S123: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S124: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S125: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S126: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S127: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S128: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],
  S129: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite115",
    ...luxurySuitesImages,
  ],

  S130: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S131: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S132: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S133: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S134: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S135: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S136: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S137: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S138: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite105",
    ...luxurySuitesImages,
  ],
  S139: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S140: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite130",
    ...luxurySuitesImages,
  ],
  S217: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S218: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S219: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S220: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S221: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S222: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S223: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S224: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S225: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],

  S226: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite226",
    ...luxurySuitesImages,
  ],

  S227: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S228: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S229: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S230: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],

  S231: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S232: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S233: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],
  S234: [
    "https://3dspaces.american3dvirtualtours.com/en/tour/suite231",
    ...luxurySuitesImages,
  ],

  // M Lounge
  RST1: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],
  RST2: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],
  RST3: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],
  RST4: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],
  RST5: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],
  RST6: [
    "https://3dspaces.american3dvirtualtours.com/tour/mlounge",
    ...mLoungeImages,
  ],

  // Loge Boxes - The Lofts
  LL1: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL10: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL2: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL3: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL4: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL5: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL6: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL7: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL8: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
  LL9: [
    "https://3dspaces.american3dvirtualtours.com/tour/lofts",
    ...logeBoxesImages,
  ],
};

export const verizonLoungeSuiteNames = Array.from(
  { length: 10 }, // Verizon Lounge Loge Boxes 1 to 10
  (_, i) => `Verizon Lounge Loge Box ${i + 1}`,
).concat(
  Array.from(
    { length: 9 }, // Sections 31 to 39
    (_, i) => `Section ${i + 31}`,
  ),
);

export const verizonLoungeLogeBoxSuiteNames = Array.from(
  { length: 10 },
  (_, i) => `Verizon Lounge Loge Box ${i + 1}`,
);

export const verizonLoungeClubSeatSuiteNames = Array.from(
  { length: 9 },
  (_, i) => `Section ${i + 31}`,
);

export const luxurySuiteNames = [
  ...Array.from({ length: 40 }, (_, i) => `Suite ${i + 101}`), // Suite 101-140
  ...Array.from({ length: 18 }, (_, i) => `Suite ${i + 217}`), // Suite 217-234
];

export const logeBoxSuiteNames = Array.from(
  { length: 10 }, // Loge Boxes 1 to 10
  (_, i) => `The Lofts by Phelps Construction Loge Box ${i + 1}`,
);

export const studioSuiteNames = [
  "Studio Suite 18",
  "Studio Suite 1A",
  "Studio Suite 1B",
  "Studio Suite 20",
  "Studio Suite 22",
  "Studio Suite 4A",
  "Studio Suite 4B",
  "Studio Suite 5",
  "Studio Suite 7",
  "Studio Suite 9",
];

export const loftTableSuiteNames = [
  "Section 23 Table 1",
  "Section 24 Table 2",
  "Section 25 Table 3",
  "Section 25 Table 4",
  "Section 26 Table 5",
  "Section 27 Table 6",
  "Section 28 Table 7",
  "Section 28 Table 8",
  "Section 29 Table 9",
  "Section 30 Table 10",
];

export const mLoungeSuiteNames = [
  "MSG Space",
  "Section 2",
  "Section 3",
  "Section 4",
  "Section 5",
  "Section 6",
];

export const clubSeatingSuiteNames = [
  "Section 23",
  "Section 24",
  "Section 25",
  "Section 26",
  "Section 27",
  "Section 28",
  "Section 29",
  "Section 30",
];

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const InventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const VerizonLoungeLogeBoxContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Verizon Lounge (Loge Box)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Prudential Center's newest premium space, The Verizon Lounge, provides
          a world-class lounge with center viewing for Devils games and
          concerts.
          <br />• 8 seats in private box in Verizon Lounge
          <br />• Access To The Lounges Private Bar & Space
          <br />• Access to VIP Entrances
          <br />• All-Inclusive Food & Non-Alcoholic Beverage
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const VerizonLoungeClubSeatContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Verizon Lounge (Club Seat)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Prudential Center's newest premium space, The Verizon Lounge, provides
          a world-class lounge with center viewing for Devils games and
          concerts.
          <br />• 2-8 premium club seats located within Verizon Lounge
          <br />• Access To The Lounges Private Bar & Space
          <br />• Access to VIP Entrances
          <br />• All-Inclusive Food & Non-Alcoholic Beverage
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const LuxurySuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Luxury Suites
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          You don't have to leave Jersey for the highest-end entertainment
          experience. Bring family, friends or colleagues to the show or game
          and enjoy from your own private suite.
          <br />• 16 to 22 luxury seats in your own private box
          <br />• Two (2) parking passes included and access to VIP entrances
          <br />• Starter Catering Package included to get your night started
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const StudioSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Studio Suites
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          If you're looking for the best sightline with a private,
          all-inclusive, VIP experience at Prudential Center our Studio Suites
          are for you!
          <br />• 6-8 premier lower-level private seats
          <br />• Enclosed private suite right near the action
          <br />• Access to VIP Entrances
          <br />• All-Inclusive Food & Non-Alcoholic Beverage
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const MLoungeContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        M Lounge (By BMW)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          M Lounge by BMW is Prudential Center's most exclusive and upscale
          experience, designed to offer unprecedented access in a vibrant
          atmosphere.
          <br />• 2-8 of the best seats at The Rock
          <br />• All-inclusive dining by our Master Chef
          <br />• Tunnel access to the New Jersey Devils on-ice entrance
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const LogeBoxSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Loge Boxes (At the Lofts)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          A private box in Prudential Center's Speakeasy, a premium space
          designed as a throwback to classic New Jersey.
          <br />• 8 seats in a private box in The Lofts
          <br />• All-inclusive dining
          <br />• Access to private lounge
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const LoftTableSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Loft Tables (At the Lofts)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          A private table in Prudential Center's Speakeasy, a premium space
          designed as a throwback to classic New Jersey.
          <br />• 4 seats at a private table in The Lofts
          <br />• All-Inclusive Food & Non-Alcoholic Beverage
          <br />• Access to VIP Entrances
          <br />• Access to private lounge
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const ClubSeatingSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Club Seating (At the Lofts)
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          A guaranteed club seat in Prudential Center's Speakeasy, a premium
          space designed as a throwback to classic New Jersey.
          <br />• 2-8 premium club seats in The Lofts
          <br />• All-Inclusive Food & Non-Alcoholic Beverage
          <br />• Access to VIP Entrances
          <br />• Access to private lounge
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export function CheckoutTerms() {
  const router = useRouterExtended();
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the Prudential Center's Suite Rental{" "}
      <Link
        onClick={(ev) => {
          ev?.preventDefault();
          router.pushSchema(routes.prudentialcenter_terms_and_conditions);
        }}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Terms & Conditions
      </Link>
      .
    </>
  );
}
